/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */

import * as React from 'react';

import { createStitches } from '@stitches/react';

import type * as Stitches from '@stitches/react';

export type { ComponentProps, CSSProperties, VariantProps } from '@stitches/react';
export type { SvgProperties } from '@stitches/react/types/css';
export type CSS = Stitches.CSS<typeof config>;

// https://adamwathan.me/dont-use-em-for-media-queries/
// Pixels are the only unit that behave consistently across all commonly used browsers.
export const mediaQueries = {
  mobile: '(min-width: 340px)',
  embedMobile: '(max-width: 430px)',
  mobileLandscape: '(min-width: 480px)',
  tablet: '(min-width: 768px)',
  tabletLandscape: '(min-width: 1024px)',
  laptop: '(min-width: 1160px)',
  desktop: '(min-width: 1440px)',
  desktopMd: '(min-width: 1600px)',
  desktopLg: '(min-width: 1920px)',
  touch: '(pointer: coarse)',
  darkMode: '(prefers-color-scheme: dark)',
  prefersReducedMotion: '(prefers-reduced-motion: reduce)',
  uploadFlow: '(min-width: 900px)',
};

export const { styled, css, globalCss, keyframes, getCssText, theme, createTheme, config, reset } =
  createStitches({
    theme: {
      fonts: {
        title: 'Acid Grotesk, sans-serif',
        base: 'Inter, sans-serif',
        kicker: 'Inter, sans-serif',
        numerals: 'NB Architekt Neue, sans-serif',

        body: 'Inter, sans-serif',
        mono: 'NB Architekt Neue, sans-serif',
      },
      widths: {
        modalWidth: '1500px',
        landingMaxWidth: '1280px',
        defaultMaxWidth: '1285px',
      },
      colors: {
        neutral50: '#FAFAFA',
        neutral100: '#F5F5F5',
        neutral200: '#E5E5E5',
        neutral300: '#D4D4D4',
        neutral400: '#A3A3A3',
        neutral500: '#737373',
        neutral600: '#525252',
        neutral700: '#404040',
        neutral800: '#262626',
        neutral900: '#171717',
        brand50: '#ECFDF5',
        brand100: '#A7F3D0',
        brand200: '#6EE7B7',
        brand300: '#34D399',
        brand500: '#10B981',
        brand600: '#059669',
        brand700: '#047857',
        brand800: '#065F46',
        brand900: '#064E3B',
        brandAccent20: '#E5F3F0',
        brandAccent40: '#D5F0E9',
        brandAccent50: '#D1FAE5',
        brandAccent60: '#C3ECE2',
        brandAccent80: '#B2E9DB',
        brandAccent100: '#A1E6D5',
        accent300: '#F0ABFC',
        accent700: '#EB5CBB',
        success50: '#F0FDF4',
        success500: '#22C55E',
        success700: '#15803D',
        warning50: '#FFF7ED',
        warning400: '#EAB308',
        warning700: '#C2410C',
        destructive50: '#FEF2F2',
        destructive200: '#F7A1A1',
        destructive300: '#EF4444',
        destructive700: '#B91C1C',
        education50: '#ECECFF',
        education100: '#8280FF',
        education200: '#605DEC',

        white: '#fff',
        black: '#0E1213',
        blue400: '#4299E1',
        grey100: '#F3F4F6',
        grey200: '#E5E6EB',
        grey300: '#D2D5D9',
        grey400: '#9DA3AE',
        grey500: '#6D7180',
        grey600: '#4D5562',
        grey700: '#394150',
        grey800: '#192024',
        grey900: '#1A1B22',
        danger100: '#e53e3e',
        danger200: '#d32f2f',
        black100: '#262626',
        gray100: '#8F8F8F',
        gray200: '#EEEEEE',
        gray300: '#E7E7E7',
        green100: '#34D399',
        green200: '#A7F3D0',

        base50: '#FAFAFA',
        base100: '#F7F7F7',
        base200: '#F5F5F5',
        base300: '#E6E6E6',
        base400: '#A3A3A3',
        base500: '#737373',
        base600: '#404040',
        base700: '#333333',
        base800: '#1F1F1F',
        base900: '#0F0F0F',

        fuchsia500: '#EB5CBB',

        userEducation600: '#605DEC',
      },
      // TODO: Update gradient color names once naming conventions are updated in Figma
      gradients: {
        green: 'linear-gradient(90deg, #34D399 1.83%, #10B981 50.07%, #059669 96.34%)',
        black: 'linear-gradient(90deg, #525252 1.83%, #404040 50.07%, #262626 96.34%)',
        white: 'linear-gradient(270deg, #fff 50%, rgba(255, 255, 255, 0) 100%)',
        fuchsia: 'linear-gradient(90deg, #FB7085 1.83%, #EB5CBB 50.07%, #D945EF 96.34%)',
      },
      space: {
        1: '4px',
        2: '8px',
        3: '12px',
        4: '16px',
        5: '20px',
        6: '24px',
        7: '28px',
        8: '32px',
        10: '40px',
        12: '48px',
        16: '64px',
        20: '80px',
        24: '96px',
        32: '128px',
        40: '160px',
        48: '192px',
      },
      radii: {
        small: '0.25rem',
        medium: '0.5rem',
        large: '0.75rem',
        extraLarge: '1rem',
        full: '100%',
        sm: '8px',
      },
      shadows: {
        brandAccent50: '0px 0px 20px rgba(161, 230, 213, 0.5)',
      },
      transitions: {
        fastest: '0.05s ease',
        fast: '0.1s ease',
        medium: '0.2s ease',
        slow: '0.3s ease',
        slowest: '0.4s ease',
      },
      scales: {
        imageZoom: 'scale(1.2)',
      },
      zIndices: {
        below: -1,
        base: 0,
        above1: 1,
        above2: 2,
        above3: 3,

        // One-offs
        // Add Z-Indices needed for new components here. Space out from others.

        // Global Components / Layout

        secondaryNav: 1200,
        header: 1300,
        tooltip: 1350,
        dropdown: 1375,
        leftNav: 1400,

        audioPlayerQueue: 1500,
        audioPlayerContainer: 1600,
        audioPlayerQueueDropdown: 1700,

        stickyFooter: 1750,

        mobileNav: 1800,

        audioPlayerSheet: 1850,

        audioPlayerComments: 1855,
        //need this because laptop and mobile layout are different
        audioPlayerQueueMobile: 1860,
        overlay: 1900,
        search: 2000,
        modal: 2100,
        toast: 2200,
        modalDropdown: 2300,

        previewFrame: 2400,
        previewFrameInfo: 2450,
      },
    },
    media: mediaQueries,
    utils: {
      // Font styles
      fontStyle: (value: string): any => {
        switch (value) {
          case 'regular':
            return {
              fontWeight: 400,
            };
          case 'medium':
            return {
              fontWeight: 500,
            };
          case 'semibold':
            return {
              fontWeight: 600,
            };
          case 'bold':
            return {
              fontWeight: 800,
            };
        }
      },

      // Font types
      title: (value: 'XXS' | 'XS' | 'S' | 'M' | 'L' | 'XL' | 'XXL'): any => {
        switch (value) {
          case 'XXS':
            return {
              fontFamily: '$title',
              fontWeight: 500,
              fontSize: 13,
              lineHeight: '17px',
              color: '$neutral800',
            };
          case 'XS':
            return {
              fontFamily: '$title',
              fontWeight: 500,
              fontSize: 14,
              lineHeight: '18px',
              color: '$neutral800',
            };
          case 'S':
            return {
              fontFamily: '$title',
              fontWeight: 500,
              fontSize: 16,
              lineHeight: '20px',
              color: '$neutral800',
            };
          case 'M':
            return {
              fontFamily: '$title',
              fontWeight: 500,
              fontSize: 18,
              lineHeight: '22px',
              color: '$neutral800',
            };
          case 'L':
            return {
              fontFamily: '$title',
              fontWeight: 500,
              fontSize: 22,
              lineHeight: '26px',
              color: '$neutral800',
            };
          case 'XL':
            return {
              fontFamily: '$title',
              fontWeight: 500,
              fontSize: 28,
              lineHeight: '34px',
              color: '$neutral800',
            };
          case 'XXL':
            return {
              fontFamily: '$title',
              fontWeight: 500,
              fontSize: 48,
              lineHeight: '54px',
              color: '$neutral800',
            };
          default:
            console.warn('value missing from utils.title');
            break;
        }
      },
      base: (value: 'XS' | 'S' | 'M' | 'L' | 'XL'): any => {
        switch (value) {
          case 'XS':
            return {
              fontFamily: '$base',
              fontSize: 11,
              lineHeight: '14px',
              color: '$neutral800',
            };
          case 'S':
            return {
              fontFamily: '$base',
              fontSize: 12,
              lineHeight: '16px',
              color: '$neutral800',
            };
          case 'M':
            return {
              fontFamily: '$base',
              fontSize: 14,
              lineHeight: '18px',
              color: '$neutral800',
            };
          case 'L':
            return {
              fontFamily: '$base',
              fontSize: 16,
              lineHeight: '20px',
              color: '$neutral800',
            };
          case 'XL':
            return {
              fontFamily: '$base',
              fontSize: 22,
              lineHeight: '26px',
              color: '$neutral800',
            };
          default:
            console.warn('value missing from utils.base');
            break;
        }
      },
      kicker: (value: 'XS' | 'S' | 'M'): any => {
        switch (value) {
          case 'XS':
            return {
              fontFamily: '$base',
              fontSize: 11,
              lineHeight: '12px',
              letterSpacing: '0.04em',
              color: '$neutral800',
            };
          case 'S':
            return {
              fontFamily: '$base',
              fontSize: 12,
              lineHeight: '14px',
              letterSpacing: '0.04em',
              color: '$neutral800',
            };
          case 'M':
            return {
              fontFamily: '$base',
              fontSize: 14,
              lineHeight: '18px',
              letterSpacing: '0.04em',
              color: '$neutral800',
            };
          default:
            console.warn('value missing from utils.kicker');
            break;
        }
      },
      numerals: (value: 'XXS' | 'XS' | 'S' | 'M' | 'L'): any => {
        switch (value) {
          case 'XXS':
            return {
              fontFamily: '$numerals',
              fontSize: 22,
              lineHeight: '22px',
              color: '$neutral800',
            };
          case 'XS':
            return {
              fontFamily: '$numerals',
              fontSize: 24,
              lineHeight: '24px',
              color: '$neutral800',
            };
          case 'S':
            return {
              fontFamily: '$numerals',
              fontSize: 28,
              lineHeight: '28px',
              color: '$neutral800',
            };
          case 'M':
            return {
              fontFamily: '$numerals',
              fontSize: 32,
              lineHeight: 'auto',
              color: '$neutral800',
            };
          case 'L':
            return {
              fontFamily: '$numerals',
              fontSize: 52,
              lineHeight: 'auto',
              color: '$neutral800',
            };
          default:
            console.warn('value missing from utils.numerals');
            break;
        }
      },

      // Shadows
      shadow: (value: string): any => {
        switch (value) {
          case 'XS':
            return {
              offset: '0px 0px rgba(0, 0, 0, 0.08)',
            };
          case 'S':
            return {
              boxShadow: '0px 2px rgba(0, 0, 0, 0.18), 0px, 0px rgba(0, 0, 0, 0.08)',
            };
          case 'M':
            return {
              boxShadow: '0px 0px 0px 1px rgba(0, 0, 0, 0.04), 0px 6px 16px rgba(0, 0, 0, 0.12)',
            };
          case 'L':
            return {
              boxShadow: '0px 8px rgba(0, 0, 0, 0.28), 0px, 0px rgba(0, 0, 0, 0.04)',
            };
          default:
            console.warn('value missing from utils.shadow');
            break;
        }
      },

      // Blurs
      blur: (value: string): any => {
        switch (value) {
          case 'none':
            return {
              radius: 10,
              offset: '0px, 8px rgba(16, 24, 40, 0.1)',
              backgroundColor: 'transparent',
            };
          case 'S':
            return {
              radius: 8,
              color: 'rgba(17, 24, 39, 0.2)',
              backgroundColor: 'rgba(17, 24, 39, 0.2)',
            };
          case 'M':
            return {
              radius: 16,
              backgroundColor: 'rgba(17, 24, 39, 0.2)',
            };
          case 'L':
            return {
              radius: 24,
              backgroundColor: 'rgba(17, 24, 39, 0.2)',
            };
          case 'XL':
            return {
              radius: 40,
              backgroundColor: 'rgba(17, 24, 39, 0.2)',
            };
          default:
            console.warn('value missing from utils.blur');
            break;
        }
      },

      // Elevations (cannot be named 'elevation' nor 'altitude' because of css reserved word)
      lift: (value: 'sharpEdge' | 'tertiary' | 'secondary' | 'primary' | 'high'): any => {
        switch (value) {
          case 'sharpEdge':
            return {
              boxShadow: '0px 0px 0px 1px rgba(0, 0, 0, 0.08)',
            };
          case 'tertiary':
            return {
              boxShadow: '0px 6px 16px 1px rgba(0, 0, 0, 0.08)',
            };
          case 'secondary':
            return {
              boxShadow: '0px 12px 12px rgba(0, 0, 0, 0.1)',
            };
          case 'primary':
            return {
              boxShadow:
                '0px 0px 0px 1px rgba(0, 0, 0, 0.04), 0px 18px 30px 10px rgba(67, 66, 68, 0.12)',
            };
          case 'high':
            return {
              boxShadow: ' 0px 4px 20px 8px rgba(67, 66, 68, 0.16)',
            };
          default:
            console.warn('value missing from utils.lift');
            break;
        }
      },

      ratio: (value: 'square' | 'portrait' | 'landscape'): any => {
        switch (value) {
          case 'square':
            return {
              aspectRatio: '1/1',
            };
          case 'portrait':
            return {
              aspectRatio: '3/2',
            };
          case 'landscape':
            return {
              aspectRatio: '4/3',
            };
          default:
            console.warn('value missing from utils.ratio');
            break;
        }
      },

      // Paddings
      paddingX: (value: Stitches.PropertyValue<'paddingLeft'>) => ({
        paddingLeft: value,
        paddingRight: value,
      }),
      paddingY: (value: Stitches.PropertyValue<'paddingTop'>) => ({
        paddingTop: value,
        paddingBottom: value,
      }),

      // Margins
      marginX: (value: Stitches.PropertyValue<'marginLeft'>) => ({
        marginLeft: value,
        marginRight: value,
      }),
      marginY: (value: Stitches.PropertyValue<'marginTop'>) => ({
        marginTop: value,
        marginBottom: value,
      }),

      // Other
      size: (value: Stitches.PropertyValue<'width'>) => ({
        width: value,
        height: value,
      }),
    },
  } as const);

/**
 * Get the css and reset the internal css representation.
 * This is very *IMPORTANT* to do as the server might handle multiple requests
 * and we don't want to have the css accumulated from previous requests
 */
export function StitchesStyle() {
  const css = getCssText();

  reset();

  return React.createElement('style', {
    id: 'stitches' as const,
    dangerouslySetInnerHTML: {
      __html: css,
    },
  });
}
